/* eslint-disable no-useless-catch */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import proj4 from 'proj4';
import { useLeafletContext } from '@react-leaflet/core';
import { useMap } from 'react-leaflet';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import L from 'leaflet';
import geoblaze from 'geoblaze';
import {
  getColorScale,
  pixelValuesToColorFn,
  popoverFormatter
} from 'helpers/leaflet-utils';

window.proj4 = proj4;

const GeotiffLayer = React.memo(
  ({ url, title, coordinates, layerVisible, legendData }) => {
    const geoTiffLayerRef = useRef();
    const context = useLeafletContext();
    const map = useMap();
    const prevUrlRef = useRef();
    const abortControllerRef = useRef(new AbortController());

    const isMobileBrowser = useMemo(
      () => /Mobi|Android|iPad|iPhone|iPod/i.test(navigator.userAgent),
      []
    );

    const handleMapClick = useCallback(
      (e, georaster) => {
        const clickedControl =
          e.originalEvent.target.closest('.leaflet-control');
        if (clickedControl) return;

        const latlng = map.mouseEventToLatLng(e.originalEvent);
        const pixelValue = geoblaze.identify(georaster, [
          latlng.lng,
          latlng.lat
        ]);

        if (pixelValue !== null) {
          const pixelColor = pixelValuesToColorFn(
            pixelValue[0],
            title,
            legendData
          );
          if (!pixelColor) return;

          const colorData = legendData || getColorScale(title);
          const legendEntry = colorData.find(
            obj => obj.bg_color?.toLowerCase() === pixelColor?.toLowerCase()
          );

          if (!legendEntry) return;

          // Access legend and value
          const range = legendEntry.pixelValue;
          const legend = legendEntry.legend;

          if (!legend) return;
          const content = popoverFormatter(
            latlng,
            pixelColor,
            legend,
            pixelValue,
            range
          );
          L.popup().setLatLng(e.latlng).setContent(content).openOn(map);
        }
      },
      [map, title, legendData]
    );

    const removeExistingLayer = useCallback(() => {
      const container = context.layerContainer || context.map;
      if (geoTiffLayerRef.current) {
        container.removeLayer(geoTiffLayerRef.current);
        geoTiffLayerRef.current = null;
        // console.log('Removed existing GeoTIFF layer');
      }
    }, [context]);

    const loadGeoTiff = useCallback(async () => {
      // console.log('Loading GeoTIFF:', url);
      removeExistingLayer();

      // if (prevUrlRef.current === url) {
      //   // console.log("URL hasn't changed, skipping load");
      //   return;
      // }

      prevUrlRef.current = url;
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      try {
        const response = await fetch(url, {
          signal: abortControllerRef.current.signal
        });
        if (!response.ok) throw new Error('Response not ok');

        const arrayBuffer = await response.arrayBuffer();
        const georaster = await parseGeoraster(arrayBuffer);

        const container = context.layerContainer || context.map;

        geoTiffLayerRef.current = new GeoRasterLayer({
          georaster,
          resolution: 256,
          opacity: layerVisible ? 0.5 : 0,
          name: 'index_layer',
          pixelValuesToColorFn: values =>
            pixelValuesToColorFn(values[0], title, legendData)
        });

        container.addLayer(geoTiffLayerRef.current);
        map.fitBounds(geoTiffLayerRef.current.getBounds());

        const clickHandler = e => handleMapClick(e, georaster);
        map.on('click', clickHandler);

        // console.log('New GeoTIFF layer added');

        return () => {
          map.off('click', clickHandler);
        };
      } catch (error) {
        if (error.name === 'AbortError') {
          // console.log('Fetch aborted');
        } else {
          // console.error('Error loading GeoTIFF:', error);
          map.fitBounds(coordinates);
        }
      } finally {
        if (isMobileBrowser) {
          map.dragging.disable();
          map.tapHold.disable();
        }
      }
    }, [
      context,
      url,
      map,
      coordinates,
      isMobileBrowser,
      handleMapClick,
      layerVisible,
      title,
      legendData,
      removeExistingLayer
    ]);

    useEffect(() => {
      const cleanup = loadGeoTiff();

      return () => {
        abortControllerRef.current.abort();
        removeExistingLayer();
        map.closePopup();
        if (cleanup && typeof cleanup.then === 'function') {
          cleanup.then(cleanupFn => cleanupFn && cleanupFn());
        }
      };
    }, [loadGeoTiff, map, removeExistingLayer]);

    useEffect(() => {
      if (geoTiffLayerRef.current) {
        geoTiffLayerRef.current.setOpacity(layerVisible ? 0.8 : 0);
      }
    }, [layerVisible]);

    return null;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.url === nextProps.url &&
      prevProps.title === nextProps.title &&
      prevProps.layerVisible === nextProps.layerVisible &&
      JSON.stringify(prevProps.legendData) ===
        JSON.stringify(nextProps.legendData)
    );
  }
);

GeotiffLayer.displayName = 'GeotiffLayer';

export default GeotiffLayer;
